/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { getFullDate } from '../../commons/CommonMethods';
import { upArrowIcon } from '../../../images/images';
import { useQueryCall } from '../../../api/react-client';
import { isPlayGroundMode } from '../../../utils/CookieToken';
import { lifelineEndPoints } from '../../../api/end-points';
import { Pause, Play, SquareArrowOutUpRight } from 'lucide-react';

const PHONE_REGEX = /\b(\+?91[\s-]?)?[6-9]\d{9}\b/;
const EMAIL_REGEX = /[\w.-]+@[\w.-]+\.\w+/;
const hasPii = (text) =>
  typeof text === 'string' &&
  (PHONE_REGEX.test(text) || EMAIL_REGEX.test(text));

import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { ToolTip } from '../../ComponentsV1/ToolTip/Tooltip';
import { handleSignout } from '../../widgets/UserWidget/WidgetComps/Logout';

const audioPlayerCSS = `
              .hide-audio-jump .rhap_main-controls .rhap_rewind-button,
              .hide-audio-jump .rhap_main-controls .rhap_forward-button {
                display: none !important;
              }
              .hide-audio-jump .rhap_main-controls .rhap_play-pause-button {
                height: auto !important;
                width: auto !important;
                display: flex !important; 
              }
              .rhap_controls-section {
              margin-right: 10px !important;
              flex: none 
              }
              .rhap_current-time {
              font-size: 1.4rem !important;
              font-weight: 500 !important;
              margin-right: 5px !important;
              }
              .rhap_total-time {
              font-size: 1.4rem !important;
              font-weight: 500 !important;
              margin-left: 5px !important;
              }
            `;

const telecomLogsHeadings = [
  { name: 'Caller', key: 1 },
  { name: 'Receiver', key: 2 },
  { name: 'Call Type', key: 3 },
  { name: 'Call Time', key: 4 },
  { name: 'Call Status', key: 5 },
  { name: 'Caller status', key: 8 },
  { name: 'Receiver status', key: 9 },
  // { name: 'Vendor name', key: 6 },
  { name: 'Call recording', key: 7 },
];

const getRecordingSrc = async (recording_url) => {
  const recordingEndPoint = `${lifelineEndPoints.GET_SECURE_RECORDING_URL}?recording_url=${recording_url}`;
  const orgId = sessionStorage.getItem('org_id') || '1';
  const response = await fetch(
    `${window.location.origin}/api/crm_proxy/${
      isPlayGroundMode ? `playground` : `lifeline_apis`
    }/${recordingEndPoint}`,
    {
      method: 'GET',
      headers: {
        'x-org-channel-id': orgId,
        'Content-Type': 'application/json',
      },
    },
  );

  if (response.status === 401) {
    return handleSignout();
  }
  const blob = await response.blob();
  if (response.ok) {
    const url = URL.createObjectURL(blob);
    return url;
  }
  if (!response.ok) {
    throw new Error(
      'Some error occurred, please try again later or reach to your POC',
    );
  }
  return Promise.reject(response);
};

const RenderRecordingTemp = ({ item }) => {
  const { recording_url, status } = item;
  const queryKey = ['get_secured_recording_url', recording_url];
  const queryConfig = { enabled: false };
  const queryFunc = () => {
    return getRecordingSrc(recording_url);
  };
  const { data: recording, refetch: getRecording } = useQueryCall(
    queryConfig,
    null,
    queryKey,
    queryFunc,
  );

  // Playback speed control
  const playerRef = React.useRef(null);
  const [playbackRate, setPlaybackRate] = React.useState(1);

  React.useEffect(() => {
    const audioEl = playerRef?.current?.audio?.current;
    if (audioEl) {
      audioEl.playbackRate = playbackRate;
    }
  }, [playbackRate, recording]);

  if (!recording_url || status === 'not_answered') {
    return <p className='red_9_bg'>No data Available.</p>;
  }
  return (
    <div className='tw-w-[140rem] tw-mr-10'>
      {recording ? (
        <div
          className='tw-w-full tw-flex tw-items-center tw-gap-4'
          css={audioPlayerCSS}
        >
          <AudioPlayer
            className='tw-rounded-xl !tw-shadow-none !tw-w-[22rem] hide-audio-jump !tw-p-0 !tw-m-0'
            layout='horizontal-reverse'
            loop={false}
            ref={playerRef}
            customIcons={{
              pause: (
                <div className='tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100 tw-w-14 tw-h-14 tw-p-2'>
                  <Pause
                    size={22}
                    className='tw-text-base-green'
                    fill={'#318616'}
                  />
                </div>
              ),
              play: (
                <div className='tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100 tw-w-14 tw-h-14 tw-p-2'>
                  <Play size={22} className='tw-text-base-green' />
                </div>
              ),
            }}
            src={recording}
            showFilledVolume={false}
            customVolumeControls={[]}
            customAdditionalControls={[]}
            showSkipControls={false}
          />
          <ToolTip content='Click to set playback speed' placement='bottom'>
            <select
              className='tw-text-[1.5rem] tw-font-medium tw-cursor-pointer tw-text-base-green tw-p-1 tw-border-base-green !tw-flex !tw-items-center tw-border-none !tw-leading-none !tw-justify-center !tw-w-fit'
              value={playbackRate}
              onChange={(e) => setPlaybackRate(Number(e.target.value))}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                background: 'none',
              }}
            >
              <option value={0.5}>0.5x</option>
              <option value={1}>1x</option>
              <option value={1.5}>1.5x</option>
              <option value={2}>2x</option>
              <option value={4}>4x</option>
            </select>
          </ToolTip>

          <ToolTip content='Open recording in a new tab' placement='bottom'>
            <span
              onClick={() => {
                if (recording) {
                  window.open(recording, '_blank', 'noopener,noreferrer');
                }
              }}
              className='tw-text-base-green tw-cursor-pointer hover:tw-scale-105 tw-transition-all tw-ease-in-out tw-flex tw-items-center tw-justify-center'
            >
              <SquareArrowOutUpRight size={18} strokeWidth={2.5} />
            </span>
          </ToolTip>
        </div>
      ) : (
        <span
          onClick={() => {
            getRecording(recording_url);
          }}
          className='tw-cursor-pointer'
        >
          Listen
          <span className='icon iconMarginL'>
            <img src={upArrowIcon} alt='' />
          </span>
        </span>
      )}
    </div>
  );
};

const getTelecomCallLogsTemplate = (item) => {
  return {
    1: {
      title: hasPii(item.call_from) ? (
        <span data-dd-privacy='mask'>{item.call_from}</span>
      ) : (
        item.call_from || '-'
      ),
    },
    2: {
      title: hasPii(item.call_to) ? (
        <span data-dd-privacy='mask'>{item.call_to}</span>
      ) : (
        item.call_to || '-'
      ),
    },
    3: {
      title: item.call_type || '-',
    },
    4: {
      title: getFullDate(item.created_at * 1000) || '-',
    },
    5: {
      title: <p className={item.status_color}>{item.status}</p> || '-',
    },
    6: {
      title: item.vendor || '-',
    },
    7: {
      title: <RenderRecordingTemp item={item} />,
    },
    8: {
      title:
        item.caller_status == null ||
        item.caller_status == undefined ||
        item.caller_status == '' ? (
          '-'
        ) : (
          <p className={item.caller_status_color}> {item.caller_status} </p>
        ),
    },
    9: {
      title:
        item.receiver_status == null ||
        item.receiver_status == undefined ||
        item.receiver_status == '' ? (
          '-'
        ) : (
          <p className={item.receiver_status_color}> {item.receiver_status} </p>
        ),
    },
  };
};

const getTelecomLogsRows = (callLogsData) => {
  const rowsData = [];
  callLogsData &&
    callLogsData.map((call) => {
      const callsObj = getTelecomCallLogsTemplate(call);
      rowsData.push(callsObj);
      return true;
    });
  return rowsData;
};

export const getTelecomLogs = (callLogsData) => {
  return {
    headings: telecomLogsHeadings,
    rows: getTelecomLogsRows(callLogsData),
  };
};
